"use client";

import { useState, useEffect } from "react";
import { loginUser, storeToken, storeUserInfo } from "@/utils/storage";
import { motion, AnimatePresence, useAnimation } from "framer-motion";
import { ArrowRight, Smartphone, KeyRound, Lock, X } from "lucide-react";
import Logo from "./Logo";

const API_BASE = process.env.NEXT_PUBLIC_API_BASE || "/api";


interface LoginProps {
    onLoginSuccess: () => void;
}

type LoginStep = "mobile" | "otp" | "invite" | "pin" | "verify-pin";

export default function Login({ onLoginSuccess }: LoginProps) {
    const [isMounted, setIsMounted] = useState(false);

    useEffect(() => {
        setIsMounted(true);
    }, []);

    const [mobile, setMobile] = useState("");
    const [otp, setOtp] = useState("");
    const [inviteCode, setInviteCode] = useState("");
    const [pin, setPin] = useState("");
    const [step, setStep] = useState<LoginStep>("mobile");
    const [error, setError] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [token, setToken] = useState<string | null>(null);
    const [devOtp, setDevOtp] = useState<string | null>(null);
    const [showOtpToast, setShowOtpToast] = useState(false);
    const [isPinSetupNeeded, setIsPinSetupNeeded] = useState(false);

    const controls = useAnimation();

    const showError = (message: string) => {
        setError(message);
        controls.start({
            x: [0, -10, 10, -10, 10, 0],
            transition: { duration: 0.4 }
        });
    };

    const handleSendOtp = async () => {
        if (mobile.length !== 10 || !/^\d{10}$/.test(mobile)) {
            showError("Please enter a valid 10-digit mobile number");
            return;
        }
        setError("");
        setIsLoading(true);

        try {
            const res = await fetch(`${API_BASE}/auth/send-otp`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ phoneNumber: mobile }),
            });
            const data = await res.json();

            if (res.ok) {
                if (data.otp) {
                    setDevOtp(data.otp);
                    setShowOtpToast(true);
                }
                setStep("otp");
            } else {
                showError(data.message || "Failed to send OTP");
            }
        } catch (err) {
            showError("Something went wrong. Please try again.");
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleVerifyOtp = async () => {
        if (otp.length !== 6) {
            showError("Please enter a 6-digit OTP");
            return;
        }
        setError("");
        setIsLoading(true);

        try {
            // Check if user exists by trying to verify OTP without invite code first
            const res = await fetch(`${API_BASE}/auth/verify-otp`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ phoneNumber: mobile, otp }),
            });
            const data = await res.json();

            if (res.ok) {
                if (data.requiresInvite) {
                    setStep("invite");
                    return;
                }

                // Existing user - either setup PIN or verify PIN
                setToken(data.token);
                if (data.user) storeUserInfo(data.user);
                setShowOtpToast(false);
                if (data.isPinSetupNeeded) {
                    setIsPinSetupNeeded(true);
                    setStep("pin");
                } else {
                    // User already has PIN, need to verify it
                    setStep("verify-pin");
                }
            } else {
                showError(data.message || "Invalid OTP");
            }
        } catch (err) {
            showError("Verification failed. Please try again.");
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleInviteCode = async () => {
        if (!inviteCode.trim()) {
            showError("Please enter an invite code");
            return;
        }
        setError("");
        setIsLoading(true);

        try {
            const res = await fetch(`${API_BASE}/auth/verify-otp`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ phoneNumber: mobile, otp, inviteCode }),
            });
            const data = await res.json();

            if (res.ok) {
                storeToken(data.token);
                if (data.user) storeUserInfo(data.user);
                setShowOtpToast(false);
                if (data.isPinSetupNeeded) {
                    setIsPinSetupNeeded(true);
                    setStep("pin");
                } else {
                    loginUser(mobile);
                    onLoginSuccess();
                }
            } else {
                showError(data.message || "Invalid invite code");
            }
        } catch (err) {
            showError("Failed to verify invite code");
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSetPin = async () => {
        if (pin.length !== 6 || !/^\d{6}$/.test(pin)) {
            showError("PIN must be exactly 6 digits");
            return;
        }
        setError("");
        setIsLoading(true);

        try {
            const res = await fetch(`${API_BASE}/auth/set-pin`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ pin }),
            });
            const data = await res.json();

            if (res.ok) {
                storeToken(token!);
                loginUser(mobile);
                onLoginSuccess();
            } else {
                showError(data.message || "Failed to set PIN");
            }
        } catch (err) {
            showError("Failed to set PIN");
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleVerifyPin = async () => {
        if (pin.length !== 6 || !/^\d{6}$/.test(pin)) {
            showError("PIN must be exactly 6 digits");
            return;
        }
        setError("");
        setIsLoading(true);

        try {
            const res = await fetch(`${API_BASE}/auth/verify-pin`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ phoneNumber: mobile, pin }),
            });
            const data = await res.json();

            if (res.ok) {
                storeToken(data.token);
                if (data.user) storeUserInfo(data.user);
                loginUser(mobile);
                onLoginSuccess();
            } else {
                showError(data.message || "Invalid PIN");
            }
        } catch (err) {
            showError("Failed to verify PIN");
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    const resetForm = () => {
        setStep("mobile");
        setMobile("");
        setOtp("");
        setInviteCode("");
        setPin("");
        setError("");
        setToken(null);
        setIsPinSetupNeeded(false);
        setDevOtp(null);
        setShowOtpToast(false);
    };

    if (!isMounted) {
        return null;
    }

    return (
        <div suppressHydrationWarning className="flex flex-col items-center justify-center h-[100dvh] w-full p-4 sm:p-6 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-slate-900 via-[#020617] to-[#020617] relative overflow-hidden supports-[height:100dvh]:h-[100dvh] supports-[height:100svh]:h-[100svh]">
            <AnimatePresence>
                {showOtpToast && devOtp && (
                    <motion.div
                        initial={{ y: -40, x: "-50%", opacity: 0 }}
                        animate={{ y: 0, x: "-50%", opacity: 1 }}
                        exit={{ y: -40, x: "-50%", opacity: 0 }}
                        transition={{ type: "spring", damping: 18, stiffness: 250 }}
                        className="fixed top-4 left-1/2 z-50 w-[min(340px,90vw)] cursor-pointer"
                        onClick={() => {
                            if (devOtp) {
                                setOtp(devOtp);
                                setShowOtpToast(false);
                            }
                        }}
                    >
                        <div className="glass border border-white/10 rounded-2xl px-4 py-3 shadow-xl shadow-black/40 flex items-center justify-between gap-3">
                            <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-full bg-primary/15 flex items-center justify-center text-primary font-semibold text-sm">OTP</div>
                                <div className="flex flex-col leading-tight">
                                    <span className="text-xs text-text-muted">Code received</span>
                                    <span className="text-lg font-semibold tracking-[0.3em] text-white">{devOtp}</span>
                                    <span className="text-[10px] text-primary/80 mt-0.5 font-medium">Tap to fill OTP</span>
                                </div>
                            </div>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowOtpToast(false);
                                }}
                                className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 text-text-muted hover:text-white transition shrink-0 flex items-center justify-center"
                                aria-label="Dismiss OTP notification"
                            >
                                <X size={16} />
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
            <motion.div
                suppressHydrationWarning
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="mb-12"
            >
                <Logo size="lg" />
            </motion.div>

            <motion.div
                suppressHydrationWarning
                layout
                className="w-full max-w-xs glass-card space-y-6 p-8"
            >
                <AnimatePresence mode="wait">
                    {step === "mobile" && (
                        <motion.div
                            key="mobile"
                            initial={{ x: -20, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            exit={{ x: -20, opacity: 0 }}
                            className="space-y-4"
                        >
                            <div>
                                <label className="flex items-center gap-2 text-xs font-semibold text-text-muted mb-2 uppercase tracking-wider">
                                    <Smartphone size={14} /> Mobile Number
                                </label>
                                <motion.input
                                    animate={controls}
                                    suppressHydrationWarning
                                    type="tel"
                                    placeholder="9999999999"
                                    className="input-field text-base py-3 text-white placeholder:text-gray-400"
                                    value={mobile}
                                    onChange={(e) => {
                                        const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                                        setMobile(val);
                                    }}
                                    maxLength={10}
                                    disabled={isLoading}
                                />
                            </div>
                            {error && <p className="text-xs text-red-400 font-medium">{error}</p>}
                            <button
                                onClick={handleSendOtp}
                                disabled={isLoading}
                                className="primary-btn flex items-center justify-center gap-2 group w-full"
                            >
                                {isLoading ? "Sending..." : "Get OTP"} {!isLoading && <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />}
                            </button>
                        </motion.div>
                    )}

                    {step === "otp" && (
                        <motion.div
                            key="otp"
                            initial={{ x: 20, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            exit={{ x: 20, opacity: 0 }}
                            className="space-y-4"
                        >
                            <div>
                                <label className="flex items-center gap-2 text-xs font-semibold text-text-muted mb-2 uppercase tracking-wider">
                                    <KeyRound size={14} /> Enter OTP
                                </label>
                                <motion.input
                                    animate={controls}
                                    suppressHydrationWarning
                                    type="text"
                                    placeholder="------"
                                    className="input-field text-base py-3 text-center tracking-[0.5em] font-mono"
                                    value={otp}
                                    onChange={(e) => setOtp(e.target.value.slice(0, 6))}
                                    maxLength={6}
                                    disabled={isLoading}
                                />
                            </div>
                            {error && <p className="text-xs text-red-400 font-medium">{error}</p>}
                            <button
                                onClick={handleVerifyOtp}
                                disabled={isLoading}
                                className="primary-btn w-full"
                            >
                                {isLoading ? "Verifying..." : "Continue"}
                            </button>
                            <button
                                onClick={resetForm}
                                disabled={isLoading}
                                className="w-full text-xs text-text-muted mt-2 hover:text-white transition-colors"
                            >
                                Change Number
                            </button>
                        </motion.div>
                    )}

                    {step === "invite" && (
                        <motion.div
                            key="invite"
                            initial={{ x: 20, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            exit={{ x: 20, opacity: 0 }}
                            className="space-y-4"
                        >
                            <div>
                                <label className="flex items-center gap-2 text-xs font-semibold text-text-muted mb-2 uppercase tracking-wider">
                                    Invite Code
                                </label>
                                <motion.input
                                    animate={controls}
                                    suppressHydrationWarning
                                    type="text"
                                    placeholder="Enter your invite code"
                                    className="input-field text-base py-3"
                                    value={inviteCode}
                                    onChange={(e) => setInviteCode(e.target.value)}
                                    disabled={isLoading}
                                />
                            </div>
                            {error && <p className="text-xs text-red-400 font-medium">{error}</p>}
                            <button
                                onClick={handleInviteCode}
                                disabled={isLoading}
                                className="primary-btn w-full"
                            >
                                {isLoading ? "Verifying..." : "Verify Invite Code"}
                            </button>
                            <button
                                onClick={resetForm}
                                disabled={isLoading}
                                className="w-full text-xs text-text-muted mt-2 hover:text-white transition-colors"
                            >
                                Start Over
                            </button>
                        </motion.div>
                    )}

                    {step === "pin" && (
                        <motion.div
                            key="pin"
                            initial={{ x: 20, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            exit={{ x: 20, opacity: 0 }}
                            className="space-y-4"
                        >
                            <div>
                                <label className="flex items-center gap-2 text-xs font-semibold text-text-muted mb-2 uppercase tracking-wider">
                                    <Lock size={14} /> {isPinSetupNeeded ? "Create PIN" : "Enter PIN"}
                                </label>
                                <p className="text-xs text-gray-400 mb-3">
                                    {isPinSetupNeeded ? "Create a 6-digit PIN for future logins" : "Enter your 6-digit PIN"}
                                </p>
                                <motion.input
                                    animate={controls}
                                    suppressHydrationWarning
                                    type="password"
                                    placeholder="------"
                                    className="input-field text-base py-3 text-center tracking-[0.5em] font-mono"
                                    value={pin}
                                    onChange={(e) => setPin(e.target.value.slice(0, 6))}
                                    maxLength={6}
                                    disabled={isLoading}
                                />
                            </div>
                            {error && <p className="text-xs text-red-400 font-medium">{error}</p>}
                            <button
                                onClick={isPinSetupNeeded ? handleSetPin : handleVerifyPin}
                                disabled={isLoading}
                                className="primary-btn w-full"
                            >
                                {isLoading ? "Processing..." : isPinSetupNeeded ? "Set PIN & Login" : "Verify PIN"}
                            </button>
                            {!isPinSetupNeeded && (
                                <button
                                    onClick={() => {
                                        setIsPinSetupNeeded(true);
                                        setPin("");
                                    }}
                                    className="w-full text-xs text-text-muted mt-2 hover:text-white transition-colors"
                                >
                                    Forgot PIN?
                                </button>
                            )}
                            <button
                                onClick={resetForm}
                                disabled={isLoading}
                                className="w-full text-xs text-text-muted mt-2 hover:text-white transition-colors"
                            >
                                Back
                            </button>
                        </motion.div>
                    )}

                    {step === "verify-pin" && (
                        <motion.div
                            key="verify-pin"
                            initial={{ x: 20, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            exit={{ x: 20, opacity: 0 }}
                            className="space-y-4"
                        >
                            <div>
                                <label className="flex items-center gap-2 text-xs font-semibold text-text-muted mb-2 uppercase tracking-wider">
                                    <Lock size={14} /> Enter Your PIN
                                </label>
                                <motion.input
                                    animate={controls}
                                    suppressHydrationWarning
                                    type="password"
                                    placeholder="------"
                                    className="input-field text-base py-3 text-center tracking-[0.5em] font-mono"
                                    value={pin}
                                    onChange={(e) => setPin(e.target.value.slice(0, 6))}
                                    maxLength={6}
                                    disabled={isLoading}
                                />
                            </div>
                            {error && <p className="text-xs text-red-400 font-medium">{error}</p>}
                            <button
                                onClick={handleVerifyPin}
                                disabled={isLoading}
                                className="primary-btn w-full"
                            >
                                {isLoading ? "Verifying..." : "Login"}
                            </button>
                            <button
                                onClick={() => {
                                    setIsPinSetupNeeded(true);
                                    setStep("pin");
                                    setPin("");
                                }}
                                className="w-full text-xs text-text-muted mt-2 hover:text-white transition-colors"
                            >
                                Forgot PIN?
                            </button>

                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.div>
        </div>
    );
}
